/** GrowthSections — Sales Rep Program + Customer Rewards landing sections. */
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Megaphone, MapPin, Trophy, ClipboardCheck, Route, Gift, Star,
  Coins, Repeat, ArrowRight, BadgePercent, GraduationCap, Award,
  BadgeCheck, FileDown, Radar,
} from "lucide-react";

const Item = ({ icon: I, title, body, d }) => (
  <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }} transition={{ delay: d, duration: 0.45 }}
    className="flex gap-3">
    <span className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center shrink-0 text-cyan-300">
      <I size={16} />
    </span>
    <div>
      <div className="text-sm font-bold text-slate-100">{title}</div>
      <div className="text-xs text-slate-400 leading-relaxed mt-0.5">{body}</div>
    </div>
  </motion.div>
);

export const GrowthSections = () => {
  const nav = useNavigate();
  return (
    <>
      {/* ---------------- SALES REP PROGRAM ---------------- */}
      <section id="rep-program" className="border-t border-white/5 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="uppercase text-xs tracking-[0.2em] font-bold text-cyan-300/80" data-testid="rep-eyebrow">
              Grow with boots on the ground
            </span>
            <h2 className="mt-3 font-heading text-4xl md:text-5xl font-black tracking-tight">
              A built-in <span className="text-[var(--ld-royal-hover)]">sales rep army</span>.
            </h2>
            <p className="mt-4 text-slate-400 leading-relaxed max-w-lg">
              Recruit door-to-door reps, hand them territories, and watch the bookings roll in —
              every knock tracked, every drop-off proven, every commission computed automatically.
            </p>
            <div className="mt-8 space-y-5">
              <Item icon={Route} d={0.05} title="Territories & optimized drop-off routes"
                body="Upload a street list — the OS batches it into optimized daily routes with turn-by-turn on the rep's phone." />
              <Item icon={ClipboardCheck} d={0.15} title="GPS + photo proof on every stop"
                body="No ghost knocks. Each completed drop-off logs location and a photo, live on your progress map." />
              <Item icon={Trophy} d={0.25} title="Leaderboard + commissions on autopilot"
                body="Reps compete on a public leaderboard; their bookings and commissions flow straight into payroll." />
            </div>
            <button onClick={() => nav("/register")} className="ld-btn ld-btn-primary mt-8" data-testid="rep-cta">
              Launch your rep program <ArrowRight size={15} />
            </button>
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }} transition={{ duration: 0.5 }}
            className="rounded-xl border border-white/10 bg-[var(--ld-surface,#141414)] p-6" data-testid="rep-mock-card">
            <div className="flex items-center gap-2 text-xs font-bold text-slate-300 mb-4">
              <Megaphone size={14} className="text-cyan-300" /> Today's rep board
            </div>
            {[["Marcus S.", "Territory 4 — Oak Cliff", "18 knocks · 3 bookings", "$96 commission"],
              ["Dee W.", "Territory 2 — Lakewood", "14 knocks · 2 bookings", "$64 commission"],
              ["Alan P.", "Territory 7 — Frisco", "11 knocks · 1 booking", "$32 commission"]].map(([n, t, k, c], i) => (
              <div key={n} className="flex items-center gap-3 py-2.5 border-t border-white/5 first:border-0">
                <span className="w-7 h-7 rounded-full bg-cyan-500/15 border border-cyan-500/30 text-cyan-300 text-[11px] font-black flex items-center justify-center">{i + 1}</span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-slate-100">{n}</div>
                  <div className="text-[10px] text-slate-500 flex items-center gap-1"><MapPin size={9} />{t}</div>
                </div>
                <div className="text-right">
                  <div className="text-[11px] text-slate-300">{k}</div>
                  <div className="text-[11px] font-bold text-emerald-300">{c}</div>
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ---------------- REP ACADEMY ---------------- */}
      <section id="academy" className="border-t border-white/5 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-24 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }} transition={{ duration: 0.5 }}
            className="order-2 lg:order-1 relative" data-testid="academy-mock-card">
            <div className="rounded-xl border border-white/10 bg-[var(--ld-surface,#141414)] p-6">
              <div className="rounded-lg border-2 border-[#c9a227] bg-[#faf7f0] p-5 text-center shadow-inner">
                <Award size={26} className="mx-auto text-[#c9a227]" />
                <div className="text-[9px] uppercase tracking-[0.3em] text-[#5a6273] mt-2">Certificate of Certification</div>
                <div className="font-heading text-lg font-black text-[#0f1f3d] mt-1">Certified Detailing Sales Rep</div>
                <div className="text-xs text-[#3d4457] mt-0.5">Marcus S. · Final score 96%</div>
                <div className="mt-3 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-black bg-emerald-600/10 border border-emerald-600/40 text-emerald-700">
                  <BadgeCheck size={11} /> VERIFIED · CERT-8A21F4
                </div>
                <div className="mt-3 flex justify-center gap-2 text-[10px] text-[#5a6273]">
                  <span className="inline-flex items-center gap-1"><FileDown size={10} /> PDF for LinkedIn</span>
                  <span>·</span><span>Public verify link</span>
                </div>
              </div>
              <div className="mt-4 pt-3 border-t border-white/5 flex items-center justify-between text-[11px]">
                <span className="text-slate-400">Marketplace: <span className="text-slate-200 font-semibold">free & paid courses</span></span>
                <span className="text-emerald-300 font-bold">Installments · Subscriptions</span>
              </div>
            </div>
            <motion.div initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: 0.5, duration: 0.45 }}
              className="absolute -bottom-5 -right-2 sm:right-6 rounded-lg border border-cyan-500/40 bg-[#0b1220] px-3.5 py-2.5 shadow-xl flex items-center gap-2"
              data-testid="academy-recruit-toast">
              <Radar size={14} className="text-cyan-300" />
              <div className="text-[10px]">
                <div className="font-bold text-cyan-200">Recruit alert — Dallas, TX</div>
                <div className="text-slate-400">A new certified rep just landed in your territory</div>
              </div>
            </motion.div>
          </motion.div>
          <div className="order-1 lg:order-2">
            <span className="uppercase text-xs tracking-[0.2em] font-bold text-emerald-300/80" data-testid="academy-eyebrow">
              Train · Certify · Recruit
            </span>
            <h2 className="mt-3 font-heading text-4xl md:text-5xl font-black tracking-tight">
              A <span className="text-emerald-300">Rep Academy</span> that mints closers.
            </h2>
            <p className="mt-4 text-slate-400 leading-relaxed max-w-lg">
              Build training courses in minutes (AI drafts them from your product docs), gate real exams
              behind them, and issue verifiable certifications — then sell your courses on the marketplace
              or recruit reps the moment they certify in your territory.
            </p>
            <div className="mt-8 space-y-5">
              <Item icon={GraduationCap} d={0.05} title="AI course builder + real exams"
                body="Upload a product PDF — AI drafts modules, lessons, and the final exam. Reps train, test, and get graded automatically." />
              <Item icon={Award} d={0.15} title="Certifications that mean something"
                body="Versioned certs with expiry and recert flows, a shareable PDF, and a public verify page anyone can check." />
              <Item icon={Radar} d={0.25} title="Recruit alerts + paid course marketplace"
                body="Get pinged the second a rep certifies in your territory. Sell your training free, one-time, installments, or subscription." />
            </div>
            <button onClick={() => nav("/academy")} className="ld-btn ld-btn-primary mt-8" data-testid="academy-cta">
              Explore the Rep Academy <ArrowRight size={15} />
            </button>
          </div>
        </div>
      </section>

      {/* ---------------- CUSTOMER REWARDS ---------------- */}
      <section id="rewards" className="border-t border-white/5 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-24 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }} transition={{ duration: 0.5 }}
            className="order-2 lg:order-1 rounded-xl border border-white/10 bg-[var(--ld-surface,#141414)] p-6" data-testid="rewards-mock-card">
            <div className="flex items-center gap-2 text-xs font-bold text-slate-300 mb-4">
              <Gift size={14} className="text-amber-300" /> Sarah's rewards wallet
            </div>
            <div className="flex items-end justify-between">
              <div>
                <div className="font-heading text-4xl font-black text-white tabular-nums">2,340 <span className="text-base text-slate-500 font-bold">pts</span></div>
                <div className="text-[11px] text-slate-500 mt-0.5">= $23.40 toward her next detail</div>
              </div>
              <span className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-[11px] font-black bg-amber-500/15 border border-amber-500/40 text-amber-300">
                <Star size={11} /> GOLD · 5% cashback
              </span>
            </div>
            <div className="mt-4 h-2 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full w-[72%] bg-gradient-to-r from-amber-400 to-amber-300" />
            </div>
            <div className="text-[10px] text-slate-500 mt-1.5">$280 more this year → PLATINUM (8% back)</div>
            <div className="mt-4 pt-3 border-t border-white/5 text-[11px] text-slate-400">
              Redeemable now: <span className="text-slate-200 font-semibold">Free interior refresh · $25 off ceramic top-up</span>
            </div>
          </motion.div>
          <div className="order-1 lg:order-2">
            <span className="uppercase text-xs tracking-[0.2em] font-bold text-amber-300/80" data-testid="rewards-eyebrow">
              Keep them coming back
            </span>
            <h2 className="mt-3 font-heading text-4xl md:text-5xl font-black tracking-tight">
              Customer rewards that <span className="text-amber-300">print repeat jobs</span>.
            </h2>
            <p className="mt-4 text-slate-400 leading-relaxed max-w-lg">
              Every dollar your customers spend earns points automatically. Tiers, cashback, and a
              rewards catalog you control — with a self-serve portal where they watch their balance grow.
            </p>
            <div className="mt-8 space-y-5">
              <Item icon={Coins} d={0.05} title="Points on every invoice, zero effort"
                body="You set points-per-dollar once. The OS applies earnings the moment an invoice is paid." />
              <Item icon={BadgePercent} d={0.15} title="Tier ladder with real cashback"
                body="Bronze to Platinum — bigger spenders unlock bigger cashback and perks you define in the catalog." />
              <Item icon={Repeat} d={0.25} title="Self-serve customer portal"
                body="Customers check balances, tiers, and redeemable rewards on their own page — and book again to earn more." />
            </div>
            <button onClick={() => nav("/register")} className="ld-btn ld-btn-primary mt-8" data-testid="rewards-cta">
              Turn on rewards <ArrowRight size={15} />
            </button>
          </div>
        </div>
      </section>
    </>
  );
};

export default GrowthSections;
